import { Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { EmptyError, Subject, takeUntil } from 'rxjs';
import { Response, WidgetFilterData } from '../../../../_shared/models';
import { Pagination } from '../../../../_shared/models/pagination-response.model';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { FilterService } from '../../../../_shared/services/filter.service';
import { ACCIDENTS_DATE_TIME_FORMAT, ACCIDENTS_EMPTY_VALUE } from '../constants/accidents-and-incidents.constants';
import { AccidentsAndIncidentsRequest } from '../models/accidents-and-incidents-request.model';

/**
 * Shared paging, filtering and loading for the Accidents & Incidents tab tables.
 * Subclasses only say which endpoint to call for a page of rows.
 */
@Directive()
export abstract class AccidentsTableBase<T> implements OnInit, OnChanges, OnDestroy {
  @Input() searchTerm: string = '';

  protected filterService: FilterService = inject(FilterService);
  protected errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);

  readonly pageSize: number = 11;
  readonly dateTimeFormat = ACCIDENTS_DATE_TIME_FORMAT;
  readonly emptyValue = ACCIDENTS_EMPTY_VALUE;

  isLoading: boolean = false;
  hasError: boolean = false;
  rows: T[] = [];
  page: number = 1;
  totalPages: number = 0;
  totalCount: number = 0;

  // Account and date filters are published by the page through the shared FilterService
  protected widgetFilter: WidgetFilterData = {} as WidgetFilterData;
  protected destroySubject: Subject<void> = new Subject<void>();
  private cancelSubject: Subject<void> = new Subject<void>();

  get showingCount(): number {
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  get hasDateRange(): boolean {
    return !!this.widgetFilter?.startDate && !!this.widgetFilter?.endDate;
  }

  protected get trimmedSearchTerm(): string | undefined {
    return this.searchTerm?.trim() || undefined;
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.hasDateRange) {
      void this.refresh();
    }

    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((filter) => {
        this.widgetFilter = filter;

        if (this.hasDateRange) {
          void this.refresh();
        }
      });
  }

  // Every input on these tables is a filter, so any later change reloads from the first page
  ngOnChanges(changes: SimpleChanges): void {
    const hasFilterChanged = Object.values(changes).some((change) => !change.firstChange);

    if (hasFilterChanged) {
      void this.refresh();
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async refresh(): Promise<void> {
    this.page = 1;
    await this.loadPage();
  }

  goToPage(page: number): void {
    this.page = page;
    void this.loadPage();
  }

  // A filed row drops out of the table, so step back first if it was the last row on its page
  protected reloadAfterRowRemoved(): void {
    if (this.rows.length === 1 && this.page > 1) {
      this.page--;
    }

    void this.loadPage();
  }

  async loadPage(): Promise<void> {
    if (!this.hasDateRange) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    try {
      this.cancelSubject.next();

      const response = await this.fetchPage({
        page: this.page,
        pageSize: this.pageSize,
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        searchTerm: this.trimmedSearchTerm
      }, this.cancelSubject);

      this.rows = response.data?.results ?? [];
      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
    } catch (error) {
      // A newer load cancelled this one and now owns the loading and error state
      if (error instanceof EmptyError) {
        return;
      }

      this.isLoading = false;
      this.hasError = true;
      this.rows = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.errorHandlerService.handleError(error, { showToast: false });
    }
  }

  protected abstract fetchPage(
    request: AccidentsAndIncidentsRequest,
    cancellation: Subject<void>
  ): Promise<Response<Pagination<T[]>>>;
}